/**
 * ONE way to change tabs, from anywhere in the page (roadmap item 38).
 *
 * Before the native tab bar there was only ever one web view, so a tab change
 * was a store write: `currentTab.set("club")`. Under the native tab bar the
 * target tab is a DIFFERENT web view — a store write would re-render the tab
 * you are already looking at and land nowhere. So every cross-tab jump goes
 * through here:
 *
 *   - native tab bar driving  -> ask the shell (`selectTab`), which selects the
 *     tab and broadcasts the new shell state to every live view.
 *   - browser / older shell   -> the store write, exactly as before.
 *
 * Under the native tab bar there is NO store fallback, deliberately. A
 * `currentTab.set()` inside a view pinned to a different tab renders the wrong
 * panel under a tab bar that says otherwise, with no pill to escape by and
 * nothing that ever re-pins it — a permanently wrong screen where a dropped tap
 * would merely have been a dropped tap. The `selectTab` method and the user
 * script that sets the flag ship in the same binary, so a shell that injected
 * the flag has the method by construction; feature-detecting it separately only
 * created a window (before the first handshake resolves) in which the fallback
 * could fire.
 *
 * The same bundle therefore runs unchanged at swimset.club, on a shell that
 * predates this work, and on one that has it.
 */
import { get } from "svelte/store";
import { currentTab, type TabId } from "./nav";
import { nativeTabsActive, shellTab } from "./nativeTabs";
import { selectedWorkoutUUID } from "./state";
import { bridge } from "../bridge/client";

export interface GoToTabOptions {
  /** Ride-along for a Scores/Club -> Swim jump: the swim to land on. */
  workoutUUID?: string | null;
}

/**
 * Where a tab change should be delivered. Pure, so the routing decision is
 * testable without a bridge: "native" = ask the shell, "store" = write
 * `currentTab`, "none" = this view IS the target tab (a no-op, not a bug —
 * e.g. the Swim view selecting a swim, which must not re-ask the shell).
 */
export function tabChangeTarget(
  tab: TabId,
  env: { nativeTabs: boolean; pinnedTab: TabId | null; carriesSwim: boolean },
): "native" | "store" | "none" {
  if (!env.nativeTabs) return "store";
  // Already the tab we are pinned to, and nothing to hand over: nothing to do.
  // A swim STILL goes out, because the other views have to learn about it.
  if (env.pinnedTab === tab && !env.carriesSwim) return "none";
  return "native";
}

/** Go to `tab`, by whichever route this shell supports. Fire-and-forget. */
export function goToTab(tab: TabId, opts: GoToTabOptions = {}): void {
  const target = tabChangeTarget(tab, {
    nativeTabs: nativeTabsActive(),
    pinnedTab: shellTab(),
    carriesSwim: typeof opts.workoutUUID === "string" && opts.workoutUUID.length > 0,
  });
  if (target === "none") return;
  if (target === "store") {
    // One web view: the Swim panel reads the selected swim from the store, so
    // the ride-along lands there too (a Sets bar's View on a pill-mode shell).
    if (typeof opts.workoutUUID === "string" && opts.workoutUUID.length > 0) {
      selectedWorkoutUUID.set(opts.workoutUUID);
    }
    if (get(currentTab) !== tab) currentTab.set(tab);
    return;
  }
  // Fire-and-forget: the shell owns the tab bar from here. A rejected call is
  // not recoverable in-page (writing the store would render the wrong panel in
  // this view), so it is swallowed the way triggerHaptic's is.
  void bridge
    .selectTab({ tab, workoutUUID: opts.workoutUUID ?? null })
    .catch(() => {});
}
